import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Text,
  Button,
  Group,
  Badge,
  Modal,
  TextInput,
  NumberInput,
  Textarea,
  Switch,
  Stack,
  LoadingOverlay,
  Grid,
  Table,
  ActionIcon,
  Card,
  Center,
  Alert,
  ScrollArea,
  ThemeIcon,
} from "@mantine/core"; // <--- ThemeIcon added here
import {
  IconPlus,
  IconEdit,
  IconCalendarTime,
  IconTrash,
  IconClock,
  IconAlertTriangle,
  IconCurrencyRupee,
} from "@tabler/icons-react";
import { useNotification } from "../../../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../../../config/baseUrls";

// --- API HELPERS ---
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

const BASE_URL =
  `${API_BASE_URL}/admin/addon-events/timeslots`;
const CONFIG_BASE_URL =
  `${API_BASE_URL}/admin/addon-events/config`;
const PRODUCT_BASE_URL =
  `${API_BASE_URL}/admin/products`;

// --- TIMESLOT MODAL ---
const AddOnEventTimeslotModal = ({ isOpen, onClose, config, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [timeslots, setTimeslots] = useState([]);
  const [generationForm, setGenerationForm] = useState({
    startDate: "",
    endDate: "",
    slotDurationMinutes: 30,
    capacityLimit: 30,
  });
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualForm, setManualForm] = useState({
    startTime: "",
    endTime: "",
    capacityLimit: 30,
  });
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    if (isOpen && config?.id) fetchTimeslots();
  }, [isOpen, config?.id]);

  const fetchTimeslots = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/${config.id}`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) setTimeslots(data.data);
    } catch (e) {
      showError("Error", "Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    const isoSuffix = ":00+05:30";
    try {
      const res = await fetch(`${BASE_URL}/generate/${config.id}`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          startDate: generationForm.startDate + isoSuffix,
          endDate: generationForm.endDate + isoSuffix,
          slotDurationMinutes: generationForm.slotDurationMinutes,
          capacityLimit: generationForm.capacityLimit,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showSuccess("Success", data.message);
        fetchTimeslots();
        onRefresh();
      } else showError("Failed", data.error);
    } catch (e) {
      showError("Error", "Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleManualAdd = async (e) => {
    e.preventDefault();
    setLoading(true);
    const isoSuffix = ":00+05:30";
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/addon-events/manual-timeslot/generate/${config.id}`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            startTime: manualForm.startTime + isoSuffix,
            endTime: manualForm.endTime + isoSuffix,
            capacityLimit: manualForm.capacityLimit,
          }),
        },
      );
      const data = await res.json();
      if (data.success) {
        showSuccess("Created", "Manual slot added");
        setManualForm({ startTime: "", endTime: "", capacityLimit: 30 });
        setShowManualForm(false);
        fetchTimeslots();
      } else showError("Failed", data.error);
    } catch (e) {
      showError("Error", "Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleSlotUpdate = async (slotId, key, value) => {
    setTimeslots((prev) =>
      prev.map((s) => (s.id === slotId ? { ...s, [key]: value } : s)),
    );
    try {
      const res = await fetch(`${BASE_URL}/${slotId}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ [key]: value }),
      });
      const data = await res.json();
      if (!data.success) {
        showError("Failed", data.error);
        fetchTimeslots();
      }
    } catch (e) {
      fetchTimeslots();
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm("Delete this slot?")) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/${slotId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        showSuccess("Deleted", "Slot removed");
        fetchTimeslots();
      } else showError("Failed", data.error);
    } catch (e) {
      showError("Error", "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const slotsSold = timeslots.some((s) => s.currentSoldCount > 0);

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={
        <Group>
          <IconClock />
          <Text fw={700}>Manage Slots: {config?.title}</Text>
        </Group>
      }
      size="xl"
      padding="lg"
    >
      <Stack gap="md">
        <LoadingOverlay
          visible={loading}
          overlayProps={{ radius: "sm", blur: 2 }}
        />
        {slotsSold && (
          <Alert
            color="red"
            icon={<IconAlertTriangle size={16} />}
            title="Slots in Use"
          >
            Bulk generation disabled because some slots have sold passes.
          </Alert>
        )}

        <Paper withBorder p="md" radius="md" bg="gray.0">
          <Text fw={600} mb="sm" size="sm" tt="uppercase" c="dimmed">
            Generator
          </Text>
          <form onSubmit={handleGenerate}>
            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Start"
                  type="datetime-local"
                  required
                  value={generationForm.startDate}
                  onChange={(e) =>
                    setGenerationForm({
                      ...generationForm,
                      startDate: e.target.value,
                    })
                  }
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="End"
                  type="datetime-local"
                  required
                  value={generationForm.endDate}
                  onChange={(e) =>
                    setGenerationForm({
                      ...generationForm,
                      endDate: e.target.value,
                    })
                  }
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput
                  label="Duration (min)"
                  min={10}
                  value={generationForm.slotDurationMinutes}
                  onChange={(v) =>
                    setGenerationForm({
                      ...generationForm,
                      slotDurationMinutes: v,
                    })
                  }
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput
                  label="Capacity"
                  min={1}
                  value={generationForm.capacityLimit}
                  onChange={(v) =>
                    setGenerationForm({ ...generationForm, capacityLimit: v })
                  }
                />
              </Grid.Col>
            </Grid>
            <Group justify="space-between" mt="md">
              <Button
                variant="subtle"
                size="xs"
                onClick={() => setShowManualForm(!showManualForm)}
              >
                {showManualForm ? "Hide Custom" : "+ Add Custom Slot"}
              </Button>
              <Button type="submit" disabled={slotsSold} color="indigo">
                Generate
              </Button>
            </Group>
          </form>
          {showManualForm && (
            <Paper withBorder p="sm" mt="md" bg="white">
              <Text size="xs" fw={700} mb="xs">
                Manual Slot
              </Text>
              <form onSubmit={handleManualAdd}>
                <Group align="end">
                  <TextInput
                    label="Start"
                    type="datetime-local"
                    required
                    value={manualForm.startTime}
                    onChange={(e) =>
                      setManualForm({
                        ...manualForm,
                        startTime: e.target.value,
                      })
                    }
                    style={{ flex: 1 }}
                  />
                  <TextInput
                    label="End"
                    type="datetime-local"
                    required
                    value={manualForm.endTime}
                    onChange={(e) =>
                      setManualForm({ ...manualForm, endTime: e.target.value })
                    }
                    style={{ flex: 1 }}
                  />
                  <NumberInput
                    label="Cap"
                    min={1}
                    value={manualForm.capacityLimit}
                    onChange={(v) =>
                      setManualForm({ ...manualForm, capacityLimit: v })
                    }
                    w={80}
                  />
                  <Button type="submit" variant="light" color="orange">
                    Add
                  </Button>
                </Group>
              </form>
            </Paper>
          )}
        </Paper>

        <Box>
          <Text fw={600} mb="xs">
            Active Slots ({timeslots.length})
          </Text>
          <ScrollArea h={300} className="border rounded-md">
            <Table stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Time</Table.Th>
                  <Table.Th>Capacity</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th align="right">Action</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {timeslots.map((slot) => (
                  <Table.Tr key={slot.id}>
                    <Table.Td>
                      <Text size="sm">
                        {new Date(slot.startTime).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}{" "}
                        -{" "}
                        {new Date(slot.endTime).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {new Date(slot.startTime).toLocaleDateString()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Badge size="sm" variant="light">
                          {slot.currentSoldCount} Sold
                        </Badge>
                        <NumberInput
                          size="xs"
                          w={60}
                          min={slot.currentSoldCount}
                          value={slot.capacityLimit}
                          onChange={(v) =>
                            handleSlotUpdate(slot.id, "capacityLimit", v)
                          }
                        />
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        size="xs"
                        checked={slot.isActive}
                        onChange={(e) =>
                          handleSlotUpdate(
                            slot.id,
                            "isActive",
                            e.currentTarget.checked,
                          )
                        }
                      />
                    </Table.Td>
                    <Table.Td align="right">
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        disabled={slot.currentSoldCount > 0}
                        onClick={() => handleDeleteSlot(slot.id)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Box>
      </Stack>
    </Modal>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const AddOnEventActivityManagementPage = ({ productId }) => {
  const [product, setProduct] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI States
  const [editMode, setEditMode] = useState(false);
  const [currentConfig, setCurrentConfig] = useState(null);
  const [showTimeslotModal, setShowTimeslotModal] = useState(false);
  const [selectedConfigForSlot, setSelectedConfigForSlot] = useState(null);

  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    if (productId) fetchData();
  }, [productId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [prodRes, confRes] = await Promise.all([
        fetch(`${PRODUCT_BASE_URL}/${productId}`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${CONFIG_BASE_URL}/${productId}`, { headers: getAuthHeaders() }),
      ]);
      const prodData = await prodRes.json();
      const confData = await confRes.json();

      if (prodData.success) setProduct(prodData.data);
      if (confData.success) setConfigs(confData.data);
    } catch (e) {
      showError("Error", "Data fetch failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (config) => {
    setCurrentConfig({
      ...config,
      price: config.price?.toString() || "0.00",
      perUserLimit: config.perUserLimit?.toString() || "1",
    });
    setEditMode(true);
  };

  const handleCreate = () => {
    setCurrentConfig({
      title: "",
      description: "",
      price: "0.00",
      isActive: true,
      perUserLimit: "1",
    });
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setCurrentConfig(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    const isNew = !currentConfig.id;
    const url = isNew
      ? `${CONFIG_BASE_URL}/${productId}`
      : `${CONFIG_BASE_URL}/${currentConfig.id}`;
    const method = isNew ? "POST" : "PUT";

    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: currentConfig.title,
          description: currentConfig.description,
          price: parseFloat(currentConfig.price),
          isActive: currentConfig.isActive,
          perUserLimit: parseInt(currentConfig.perUserLimit),
        }),
      });
      const data = await res.json();
      if (data.success) {
        showSuccess("Saved", `Config ${isNew ? "created" : "updated"}`);
        handleCancelEdit();
        fetchData();
      } else showError("Failed", data.error);
    } catch (e) {
      showError("Error", "Save failed");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !product)
    return (
      <Center h={200}>
        <Text>Loading Activity Data...</Text>
      </Center>
    );

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Box>
          <Text size="lg" fw={700}>
            Activities & Add-Ons
          </Text>
          <Text size="sm" c="dimmed">
            Manage extra activities for {product?.title}
          </Text>
        </Box>
        <Button
          leftSection={<IconPlus size={16} />}
          color="green"
          disabled={editMode}
          onClick={handleCreate}
        >
          New Activity
        </Button>
      </Group>

      {editMode && (
        <Paper withBorder p="lg" radius="md" bg="green.0">
          <Text fw={600} mb="md" c="green.9">
            {currentConfig.id ? "Edit Activity" : "Create New Activity"}
          </Text>
          <form onSubmit={handleSave}>
            <Stack>
              <Group grow>
                <TextInput
                  label="Title"
                  required
                  value={currentConfig.title}
                  onChange={(e) =>
                    setCurrentConfig({
                      ...currentConfig,
                      title: e.target.value,
                    })
                  }
                />
                <NumberInput
                  label="Price"
                  leftSection={<IconCurrencyRupee size={14} />}
                  min={0}
                  precision={2}
                  value={currentConfig.price}
                  onChange={(v) =>
                    setCurrentConfig({ ...currentConfig, price: v })
                  }
                />
              </Group>
              <Textarea
                label="Description"
                value={currentConfig.description}
                onChange={(e) =>
                  setCurrentConfig({
                    ...currentConfig,
                    description: e.target.value,
                  })
                }
              />
              <Group grow>
                <NumberInput
                  label="Max Passes Per User"
                  min={1}
                  value={currentConfig.perUserLimit}
                  onChange={(v) =>
                    setCurrentConfig({ ...currentConfig, perUserLimit: v })
                  }
                />
                <Switch
                  label="Activity Active"
                  checked={currentConfig.isActive}
                  onChange={(e) =>
                    setCurrentConfig({
                      ...currentConfig,
                      isActive: e.currentTarget.checked,
                    })
                  }
                  mt={24}
                />
              </Group>
              <Group justify="flex-end">
                <Button variant="default" onClick={handleCancelEdit}>
                  Cancel
                </Button>
                <Button type="submit" color="green" loading={loading}>
                  Save Activity
                </Button>
              </Group>
            </Stack>
          </form>
        </Paper>
      )}

      <Stack>
        {configs.length === 0 ? (
          <Paper withBorder p="xl" ta="center" bg="gray.0">
            <ThemeIcon
              size={48}
              radius="xl"
              color="gray"
              variant="light"
              mb="sm"
            >
              <IconCalendarTime size={24} />
            </ThemeIcon>
            <Text c="dimmed">No add-on activities configured yet.</Text>
          </Paper>
        ) : (
          configs.map((cfg) => (
            <Card key={cfg.id} withBorder shadow="sm" radius="md" padding="md">
              <Group justify="space-between" align="start">
                <Box>
                  <Group gap="xs">
                    <Text fw={600} size="lg">
                      {cfg.title}
                    </Text>
                    {cfg.isActive ? (
                      <Badge color="green" size="sm">
                        Active
                      </Badge>
                    ) : (
                      <Badge color="red" size="sm">
                        Inactive
                      </Badge>
                    )}
                  </Group>
                  <Text size="sm" c="dimmed" lineClamp={2} mt={4}>
                    {cfg.description}
                  </Text>
                  <Text size="sm" fw={600} mt={8}>
                    ₹{parseFloat(cfg.price).toLocaleString()}
                  </Text>
                </Box>
                <Stack gap="xs" align="flex-end">
                  <Button
                    size="xs"
                    variant="light"
                    color="indigo"
                    leftSection={<IconClock size={14} />}
                    onClick={() => {
                      setSelectedConfigForSlot(cfg);
                      setShowTimeslotModal(true);
                    }}
                  >
                    Manage Slots
                  </Button>
                  <Button
                    size="xs"
                    variant="subtle"
                    color="blue"
                    leftSection={<IconEdit size={14} />}
                    onClick={() => handleEdit(cfg)}
                    disabled={editMode}
                  >
                    Edit
                  </Button>
                </Stack>
              </Group>
            </Card>
          ))
        )}
      </Stack>

      {showTimeslotModal && selectedConfigForSlot && (
        <AddOnEventTimeslotModal
          isOpen={showTimeslotModal}
          onClose={() => setShowTimeslotModal(false)}
          config={selectedConfigForSlot}
          onRefresh={fetchData}
        />
      )}
    </Stack>
  );
};

export default AddOnEventActivityManagementPage;
